import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, API_ROOT } from "@/lib/api";
import { useState, useEffect, useMemo } from "react";
import { Search, Plus, Star, Download, FileText, Grid3x3, Table2, Save, X, Trash2, Loader2 } from "lucide-react";
import { TID } from "@/constants/testIds";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STATES = ["Tutti", "Lead", "Interessato", "Demo prenotata", "Demo effettuata",
                "Follow-up", "In trattativa", "Cliente", "Cliente Premium", "Cliente perso", "Non interessato"];
const PRIORITIES = ["Bassa", "Media", "Alta", "Urgente"];
const SOURCES = ["Instagram", "LinkedIn", "Facebook", "WhatsApp", "Webinar", "Evento",
                 "Referral", "Google", "Passaparola", "Cliente", "Campagna ADS", "Manuale", "Newsletter manuale"];

export default function CRMPage() {
    const [q, setQ] = useState("");
    const [state, setState] = useState("");
    const [mode, setMode] = useState("list"); // "list" | "table"
    const [showAdd, setShowAdd] = useState(false);
    const [showExport, setShowExport] = useState(false);
    const qc = useQueryClient();

    const clients = useQuery({
        queryKey: ["clients", q, state],
        queryFn: () => api.get(`/clients?q=${encodeURIComponent(q)}&state=${encodeURIComponent(state)}&limit=500`).then((r) => r.data),
    });

    const create = useMutation({
        mutationFn: (body) => api.post("/clients", body).then((r) => r.data),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["clients"] }); toast.success("Contatto creato."); setShowAdd(false); },
    });

    const items = clients.data || [];

    return (
        <div className="space-y-6 pv-enter">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="pv-eyebrow mb-2">CRM · Contatti</div>
                    <h1 className="pv-serif text-4xl tracking-tight">{items.length} <span className="text-textmuted">contatti</span></h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1 rounded-xl border border-border/[var(--border-opacity)] bg-surface p-1">
                        <button
                            onClick={() => setMode("list")}
                            data-testid="crm-mode-list"
                            className={`flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded-lg transition-colors ${
                                mode === "list" ? "bg-primary/[0.08] text-primary" : "text-textmuted hover:text-textmain"
                            }`}
                        ><Grid3x3 size={13} strokeWidth={1.5} /> Lista</button>
                        <button
                            onClick={() => setMode("table")}
                            data-testid="crm-mode-table"
                            className={`flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded-lg transition-colors ${
                                mode === "table" ? "bg-primary/[0.08] text-primary" : "text-textmuted hover:text-textmain"
                            }`}
                        ><Table2 size={13} strokeWidth={1.5} /> Tabella Excel</button>
                    </div>
                    <button
                        onClick={() => setShowExport(true)}
                        data-testid="crm-export-pdf-btn"
                        className="flex items-center gap-1.5 rounded-xl border border-border/[var(--border-opacity)] px-3 py-2 text-[12.5px] hover:bg-surface2 transition-colors"
                    ><FileText size={13} strokeWidth={1.5} /> Esporta PDF</button>
                    <button
                        onClick={() => setShowAdd(true)}
                        data-testid={TID.crmAddClient}
                        className="flex items-center gap-1.5 bg-primary text-primary-foreground rounded-xl px-3.5 py-2 text-[13px] font-medium hover:opacity-90 transition-opacity"
                    ><Plus size={14} strokeWidth={1.5} /> Nuovo contatto</button>
                </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-2 rounded-xl border border-border/[var(--border-opacity)] bg-surface px-3 py-2 flex-1 min-w-[220px] max-w-md">
                    <Search size={14} strokeWidth={1.5} className="text-textmuted" />
                    <input
                        data-testid={TID.crmSearchInput}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Cerca nome, studio, città, email..."
                        className="flex-1 bg-transparent outline-none text-[13px] placeholder:text-textsubtle"
                    />
                </div>
                <div className="flex items-center gap-1 rounded-xl border border-border/[var(--border-opacity)] bg-surface p-1 overflow-x-auto pv-scroll">
                    {STATES.map((s) => (
                        <button
                            key={s}
                            onClick={() => setState(s === "Tutti" ? "" : s)}
                            className={`text-[11.5px] px-2.5 py-1 rounded-lg whitespace-nowrap transition-colors ${
                                (state === "" && s === "Tutti") || state === s
                                    ? "bg-primary/[0.10] text-primary" : "text-textmuted hover:text-textmain"
                            }`}
                        >{s}</button>
                    ))}
                </div>
            </div>

            {mode === "list" && <ListView items={items} />}
            {mode === "table" && <EditableGrid items={items} onCreate={() => setShowAdd(true)} />}

            {showAdd && <AddClientDialog onClose={() => setShowAdd(false)} onSave={(b) => create.mutate(b)} busy={create.isPending} />}
            {showExport && <ExportPdfDialog onClose={() => setShowExport(false)} totalItems={items.length} />}
        </div>
    );
}

function ListView({ items }) {
    return (
        <div className="pv-card overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-border/[var(--border-opacity)] text-[11px] uppercase tracking-[0.14em] text-textsubtle">
                <div className="col-span-3">Nome · Studio</div>
                <div className="col-span-2">Città</div>
                <div className="col-span-2">Stato</div>
                <div className="col-span-2">Origine</div>
                <div className="col-span-1">Score</div>
                <div className="col-span-1">Star</div>
                <div className="col-span-1"></div>
            </div>
            <div className="divide-y divide-border/[var(--border-opacity)]">
                {items.map((c) => (
                    <Link
                        key={c.id}
                        to={`/crm/${c.id}`}
                        data-testid={TID.crmClientRow(c.id)}
                        className="grid grid-cols-12 gap-3 px-5 py-3.5 items-center hover:bg-primary/[0.04] transition-colors"
                    >
                        <div className="col-span-3 min-w-0">
                            <div className="text-[13.5px] font-medium truncate">{c.first_name} {c.last_name}</div>
                            <div className="text-[11.5px] text-textsubtle truncate">{c.studio}</div>
                        </div>
                        <div className="col-span-2 text-[12.5px] text-textmuted truncate">{c.city}</div>
                        <div className="col-span-2"><StateBadge state={c.state} /></div>
                        <div className="col-span-2 text-[12px] text-textmuted truncate">{c.source}</div>
                        <div className="col-span-1 pv-mono text-[12.5px]">{c.lead_score}</div>
                        <div className="col-span-1 flex items-center gap-0.5">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Star key={i} size={11} strokeWidth={1.5}
                                      className={i < c.stars ? "fill-warning text-warning" : "text-textsubtle/40"} />
                            ))}
                        </div>
                    </Link>
                ))}
                {items.length === 0 && (
                    <div className="px-5 py-16 text-center text-[13px] text-textsubtle">Nessun contatto trovato.</div>
                )}
            </div>
        </div>
    );
}

const GRID_COLS = [
    { key: "first_name", label: "Nome", w: "min-w-[120px]" },
    { key: "last_name", label: "Cognome", w: "min-w-[120px]" },
    { key: "studio", label: "Studio", w: "min-w-[180px]" },
    { key: "email", label: "Email", w: "min-w-[200px]", type: "email" },
    { key: "phone", label: "Telefono", w: "min-w-[140px]" },
    { key: "city", label: "Città", w: "min-w-[120px]" },
    { key: "state", label: "Stato", w: "min-w-[140px]", type: "select", options: STATES.filter((s) => s !== "Tutti") },
    { key: "source", label: "Origine", w: "min-w-[130px]", type: "select", options: SOURCES },
    { key: "priority", label: "Priorità", w: "min-w-[100px]", type: "select", options: PRIORITIES },
    { key: "stars", label: "★", w: "min-w-[90px]", type: "stars" },
    { key: "newsletter_subscribed", label: "Newsletter", w: "min-w-[100px]", type: "bool" },
    { key: "notes", label: "Note", w: "min-w-[220px]" },
];

function EditableGrid({ items, onCreate }) {
    const qc = useQueryClient();
    const [dirty, setDirty] = useState({}); // {id: {field: value}}
    const [saving, setSaving] = useState(false);
    const [flash, setFlash] = useState({}); // {`${id}:${key}`: true}

    const rows = useMemo(() => items.map((c) => ({ ...c, ...(dirty[c.id] || {}) })), [items, dirty]);

    const setCell = (id, key, value) => {
        setDirty((d) => ({ ...d, [id]: { ...(d[id] || {}), [key]: value } }));
        setFlash((f) => ({ ...f, [`${id}:${key}`]: true }));
        setTimeout(() => setFlash((f) => { const n = { ...f }; delete n[`${id}:${key}`]; return n; }), 700);
    };

    const save = async () => {
        const batch = Object.entries(dirty).map(([id, fields]) => ({ id, fields }));
        if (!batch.length) return;
        setSaving(true);
        try {
            await api.post("/clients/bulk-update", batch);
            setDirty({});
            qc.invalidateQueries({ queryKey: ["clients"] });
            toast.success(`${batch.length} contatti aggiornati.`);
        } catch (e) {
            toast.error("Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    // Warn if user tries to leave with unsaved changes
    useEffect(() => {
        const handler = (e) => {
            if (Object.keys(dirty).length) { e.preventDefault(); e.returnValue = ""; }
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    const dirtyCount = Object.keys(dirty).length;

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-[12px] text-textsubtle">
                    Clicca su una cella per modificarla. Le modifiche vengono salvate con il pulsante <b>Salva modifiche</b>.
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={onCreate}
                        className="flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded-lg hover:bg-surface2 text-textmuted"
                    ><Plus size={12} strokeWidth={1.5} /> Aggiungi riga</button>
                    <button
                        onClick={save}
                        disabled={!dirtyCount || saving}
                        data-testid="crm-grid-save-btn"
                        className={`flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg font-medium transition-opacity ${
                            dirtyCount ? "bg-primary text-primary-foreground hover:opacity-90" : "bg-surface2 text-textsubtle"
                        }`}
                    >
                        {saving ? <Loader2 size={12} strokeWidth={1.5} className="animate-spin" /> : <Save size={12} strokeWidth={1.5} />}
                        {saving ? "Salvataggio..." : dirtyCount ? `Salva ${dirtyCount} modifiche` : "Nessuna modifica"}
                    </button>
                </div>
            </div>

            <div className="pv-card overflow-hidden">
                <div className="overflow-x-auto pv-scroll">
                    <table className="w-full text-[12.5px]" data-testid="crm-editable-grid">
                        <thead>
                            <tr className="border-b border-border/[var(--border-opacity)] text-[11px] uppercase tracking-[0.14em] text-textsubtle">
                                {GRID_COLS.map((c) => (
                                    <th key={c.key} className={`text-left px-2.5 py-2.5 ${c.w}`}>{c.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/[var(--border-opacity)]">
                            {rows.map((r) => (
                                <tr key={r.id} className="hover:bg-primary/[0.02] transition-colors">
                                    {GRID_COLS.map((c) => (
                                        <Cell key={c.key} row={r} col={c}
                                              flash={flash[`${r.id}:${c.key}`]}
                                              onChange={(v) => setCell(r.id, c.key, v)} />
                                    ))}
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan={GRID_COLS.length} className="px-5 py-16 text-center text-textsubtle">Nessun contatto.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Cell({ row, col, onChange, flash }) {
    const v = row[col.key];
    const cellCls = `px-2.5 py-1 ${col.w} ${flash ? "bg-primary/[0.10]" : ""} transition-colors`;

    if (col.type === "select") {
        return (
            <td className={cellCls}>
                <select
                    value={v ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full bg-transparent text-[12.5px] py-1 rounded outline-none hover:bg-surface2 focus:bg-surface2 focus:pv-ring-primary px-1"
                >
                    <option value="">—</option>
                    {col.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            </td>
        );
    }
    if (col.type === "bool") {
        return (
            <td className={cellCls}>
                <button
                    onClick={() => onChange(!v)}
                    className={`text-[11px] px-2 py-0.5 rounded-md transition-colors ${
                        v ? "bg-success/10 text-success" : "bg-textsubtle/10 text-textmuted"
                    }`}
                >{v ? "Sì" : "No"}</button>
            </td>
        );
    }
    if (col.type === "stars") {
        return (
            <td className={cellCls}>
                <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <button key={i} onClick={() => onChange(i)} className="p-0.5">
                            <Star size={11} strokeWidth={1.5}
                                  className={i <= (v || 0) ? "fill-warning text-warning" : "text-textsubtle/30 hover:text-warning/60"} />
                        </button>
                    ))}
                </div>
            </td>
        );
    }
    return (
        <td className={cellCls}>
            <input
                type={col.type || "text"}
                value={v ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-transparent text-[12.5px] py-1 rounded outline-none hover:bg-surface2 focus:bg-surface2 focus:pv-ring-primary px-1"
            />
        </td>
    );
}

export function StateBadge({ state }) {
    const map = {
        "Lead": "bg-textsubtle/10 text-textmuted",
        "Interessato": "bg-azure/10 text-azure",
        "Demo prenotata": "bg-primary/10 text-primary",
        "Demo effettuata": "bg-primary/15 text-primary",
        "Follow-up": "bg-warning/10 text-warning",
        "In trattativa": "bg-opportunity/10 text-opportunity",
        "Cliente": "bg-success/10 text-success",
        "Cliente Premium": "bg-success/15 text-success",
        "Cliente perso": "bg-critical/10 text-critical",
        "Non interessato": "bg-textsubtle/10 text-textsubtle",
    };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${map[state] || "bg-textsubtle/10 text-textmuted"}`}>
            {state || "—"}
        </span>
    );
}

function AddClientDialog({ onClose, onSave, busy }) {
    const [form, setForm] = useState({
        first_name: "", last_name: "", studio: "", email: "", phone: "", city: "",
        state: "Lead", source: "Manuale", newsletter_subscribed: true,
    });
    const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
            <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-lg pv-card p-6 shadow-2xl pv-enter">
                <div className="pv-eyebrow mb-2">Nuovo contatto</div>
                <h3 className="pv-serif text-2xl mb-5">Aggiungi contatto al CRM</h3>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Nome" v={form.first_name} on={change("first_name")} />
                        <Field label="Cognome" v={form.last_name} on={change("last_name")} />
                    </div>
                    <Field label="Studio" v={form.studio} on={change("studio")} />
                    <Field label="Email" v={form.email} on={change("email")} />
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Telefono" v={form.phone} on={change("phone")} />
                        <Field label="Città" v={form.city} on={change("city")} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <div className="pv-eyebrow mb-1">Stato</div>
                            <select value={form.state} onChange={change("state")} className="w-full rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[13px]">
                                {STATES.filter((s) => s !== "Tutti").map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <div className="pv-eyebrow mb-1">Origine</div>
                            <select value={form.source} onChange={change("source")} className="w-full rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[13px]">
                                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-[12.5px]">
                        <input type="checkbox" checked={form.newsletter_subscribed} onChange={(e) => setForm((f) => ({ ...f, newsletter_subscribed: e.target.checked }))} />
                        Iscritto alla newsletter
                    </label>
                </div>
                <div className="mt-6 flex items-center justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-2 text-[13px] rounded-lg hover:bg-surface2 text-textmuted">Annulla</button>
                    <button
                        onClick={() => onSave(form)}
                        disabled={busy || !form.first_name || !form.last_name || !form.email}
                        className="px-3.5 py-2 text-[13px] rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
                    >
                        {busy ? "Creazione..." : "Crea contatto"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, v, on }) {
    return (
        <label className="block">
            <div className="pv-eyebrow mb-1">{label}</div>
            <input value={v} onChange={on} className="w-full rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[13px] focus:border-primary focus:pv-ring-primary outline-none" />
        </label>
    );
}

function ExportPdfDialog({ onClose, totalItems }) {
    const cols = useQuery({ queryKey: ["crm-columns"], queryFn: () => api.get("/crm/columns").then((r) => r.data) });
    const [selected, setSelected] = useState(new Set(["first_name", "last_name", "studio", "email", "phone", "city", "state", "source", "lead_score"]));
    const [busy, setBusy] = useState(false);

    const toggle = (k) => setSelected((s) => {
        const n = new Set(s);
        if (n.has(k)) n.delete(k); else n.add(k);
        return n;
    });

    const download = async () => {
        setBusy(true);
        try {
            const token = localStorage.getItem("pv_token");
            const res = await fetch(`${API_ROOT}/clients/export-pdf`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json",
                           ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                body: JSON.stringify({ columns: Array.from(selected), title: "CRM · Prime Vision" }),
            });
            if (!res.ok) throw new Error("Errore export");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `prime-vision-crm-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("PDF scaricato.");
            onClose();
        } catch (e) {
            toast.error("Errore export PDF");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
            <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-md pv-card p-6 pv-enter">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="pv-eyebrow mb-1">Export PDF</div>
                        <h3 className="pv-serif text-2xl">{totalItems} contatti</h3>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-surface2 text-textmuted flex items-center justify-center"><X size={15} strokeWidth={1.5} /></button>
                </div>
                <div className="text-[12.5px] text-textmuted mb-3">Seleziona le colonne da includere nel PDF ({selected.size} selezionate):</div>
                <div className="max-h-[42vh] overflow-y-auto pv-scroll space-y-1 border border-border/[var(--border-opacity)] rounded-xl p-2">
                    {(cols.data || []).map((c) => (
                        <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-surface2 cursor-pointer text-[12.5px]">
                            <input
                                type="checkbox"
                                data-testid={`export-col-${c.key}`}
                                checked={selected.has(c.key)}
                                onChange={() => toggle(c.key)}
                            />
                            <span>{c.label}</span>
                            <span className="text-[10px] text-textsubtle ml-auto pv-mono">{c.key}</span>
                        </label>
                    ))}
                </div>
                <div className="mt-6 flex items-center justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-2 text-[13px] text-textmuted">Annulla</button>
                    <button
                        onClick={download}
                        disabled={busy || selected.size === 0}
                        data-testid="crm-export-pdf-confirm"
                        className="flex items-center gap-1.5 px-3.5 py-2 text-[13px] rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
                    >
                        {busy ? <Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> : <Download size={13} strokeWidth={1.5} />}
                        {busy ? "Generazione..." : "Scarica PDF"}
                    </button>
                </div>
            </div>
        </div>
    );
}
